/* === js/utils/geolocation.js === */
/**
 * getAccurateLocation() replaces one-shot getCurrentPosition() with a short
 * watchPosition() loop: keep listening as fixes arrive, track the best (lowest)
 * accuracy seen, and resolve early once a "good enough" reading arrives —
 * otherwise resolve with the best sample once maxWaitMs is hit.
 *
 * @param {Object} [options]
 * @param {number} [options.desiredAccuracy=30] - meters; resolve early once a fix is at least this good
 * @param {number} [options.maxWaitMs=8000] - hard cap; resolve with best-so-far sample when hit
 * @param {number} [options.timeout=15000] - per-fix timeout passed to watchPosition
 * @returns {Promise<GeolocationPosition>}
 */
function getAccurateLocation(options) {
    options = options || {};
    var desiredAccuracy = options.desiredAccuracy != null ? options.desiredAccuracy : 30;
    var maxWaitMs = options.maxWaitMs != null ? options.maxWaitMs : 8000;
    var timeout = options.timeout != null ? options.timeout : 15000;

    return new Promise(function (resolve, reject) {
        var bestFix = null;
        var watchId = null;
        var maxWaitTimer = null;
        var lastError = null;
        var settled = false;

        function cleanup() {
            if (watchId !== null) {
                navigator.geolocation.clearWatch(watchId);
                watchId = null;
            }
            if (maxWaitTimer !== null) {
                clearTimeout(maxWaitTimer);
                maxWaitTimer = null;
            }
        }

        function settleResolve(position) {
            if (settled) return;
            settled = true;
            cleanup();
            resolve(position);
        }

        function settleReject(error) {
            if (settled) return;
            settled = true;
            cleanup();
            reject(error);
        }

        maxWaitTimer = setTimeout(function () {
            if (bestFix) {
                settleResolve(bestFix);
            } else if (lastError) {
                settleReject(lastError);
            } else {
                settleReject({ code: 3, message: 'Timeout expired' });
            }
        }, maxWaitMs);

        watchId = navigator.geolocation.watchPosition(
            function (position) {
                if (!bestFix || position.coords.accuracy < bestFix.coords.accuracy) {
                    bestFix = position;
                }
                if (position.coords.accuracy <= desiredAccuracy) {
                    settleResolve(bestFix);
                }
            },
            function (error) {
                if (bestFix) {
                    settleResolve(bestFix);
                } else {
                    lastError = error;
                    settleReject(error);
                }
            },
            { enableHighAccuracy: true, maximumAge: 0, timeout: timeout }
        );
    });
}

/**
 * Centralizes the PERMISSION_DENIED / POSITION_UNAVAILABLE / TIMEOUT -> user-facing
 * message mapping. Switches on numeric error.code (1/2/3 per the W3C spec's fixed
 * values), not symbolic property comparisons, so it works uniformly for real
 * GeolocationPositionError instances, the synthesized {code: 3} timeout fallback,
 * and hand-rolled test mocks.
 *
 * @param {{code: number}} error
 * @returns {string}
 */
function formatGeolocationError(error) {
    var code = error && error.code;
    switch (code) {
        case 1:
            return 'Location access denied. Please enable location permissions.';
        case 2:
            return 'Location information unavailable.';
        case 3:
            return 'Location request timed out. Please try again.';
        default:
            return 'Could not get your location';
    }
}

if (typeof module !== 'undefined' && module.exports) {
    module.exports = { getAccurateLocation: getAccurateLocation, formatGeolocationError: formatGeolocationError };
}

;
/* === js/map.js === */
/* ==================== map.js ==================== */
// Save this as: js/map.js

/**
 * Map display and interactions
 *
 * LOADED BY: app.html / app.ejs via <script src="/js/map.js">
 * USED BY: main-server.js → initMap() on tab switch, updateMapMarkers() on data load
 * DEPENDS ON: config-frontend.js (CONFIG.MAP_DEFAULTS), Leaflet.js (L global)
 * EXPORTS: initMap(), updateMapMarkers(), fishingMap, mapMarkers as globals
 */

let fishingMap = null;
let mapDefaultBounds = null;
let currentLayer = 'voyager'; // Default layer
let baseLayers = {}; // Store base layer references
let mapMarkers = []; // Store marker references for dynamic sizing
let markerGroup = null; // L.featureGroup for batch marker add/remove
let userLocationMarker = null; // Store user location marker
let baitShopGroup = null; // L.featureGroup for batch bait shop add/remove

// Icon cache: key = `${type}_${size}` → L.icon instance (avoids recreation on zoom)
const iconCache = new Map();
let lastZoomPinSize = null;
let lastZoomBaitShopSize = null;

// Cached DOM references (populated once on first use)
let _cachedSidebar = null;
let _cachedSidebarContent = null;
let _cachedDefaultZoom = null;

// Cancels any in-flight detail fetch when a new sidebar opens
let _sidebarDetailController = null;

/**
 * Keep drags, double-clicks and wheel scrolls inside an element placed in #map from
 * panning/zooming the map. `click` must keep bubbling: [data-upgrade], the outside-click
 * closers and other handlers are delegated on document.
 * @param {HTMLElement} el
 */
function isolateFromMapGestures(el) {
    L.DomEvent.disableScrollPropagation(el);
    L.DomEvent.on(el, 'mousedown touchstart pointerdown dblclick', L.DomEvent.stopPropagation);
}

function getSidebarElements() {
    if (!_cachedSidebar) {
        _cachedSidebar = document.getElementById('mapSidebar');
        _cachedSidebarContent = document.getElementById('mapSidebarContent');
    }
    return { sidebar: _cachedSidebar, sidebarContent: _cachedSidebarContent };
}

/**
 * Build a local forecast array without touching window.fishingForecast.
 * @param {Array|null} hourlyPeriods
 * @param {Array|null} tideData
 * @returns {Array}
 */
function computeLocalForecast(hourlyPeriods, tideData) {
    if (!Array.isArray(hourlyPeriods) || !hourlyPeriods.length) return [];
    if (typeof convertNWSPeriodsToForecast !== 'function') return [];
    const periods = hourlyPeriods.slice(0, APP_TIMING.MAX_FORECAST_PERIODS);
    return convertNWSPeriodsToForecast(periods, tideData) || [];
}

// ---------------------------------------------------------------------------
// FBI forecast localStorage cache — preserves historical slots across fetches
// so the chart shows the full day even though NWS only returns future periods.
// Key: fbi_fc_{locationKey}  Value: { date: 'YYYY-MM-DD', slots: [{ts, fbiScore}] }
// Expires automatically at midnight (date check on read).
// ---------------------------------------------------------------------------

const _FBI_FC_PREFIX = 'fbi_fc_';

function _todayDateStr() {
    return new Date().toISOString().slice(0, 10);
}

function loadForecastCache(locationKey) {
    try {
        const raw = localStorage.getItem(_FBI_FC_PREFIX + locationKey);
        if (!raw) return [];
        const { date, slots } = JSON.parse(raw);
        if (date !== _todayDateStr()) return []; // new day — treat as expired
        return (slots || []).map(s => ({ timestamp: new Date(s.ts), fbiScore: s.fbiScore }));
    } catch (e) {
        return [];
    }
}

function saveForecastCache(locationKey, slots) {
    try {
        localStorage.setItem(_FBI_FC_PREFIX + locationKey, JSON.stringify({
            date: _todayDateStr(),
            slots: slots.map(s => ({
                ts: s.timestamp instanceof Date ? s.timestamp.getTime() : new Date(s.timestamp).getTime(),
                fbiScore: s.fbiScore
            }))
        }));
    } catch (e) {
        // Quota exceeded or storage unavailable — silently skip
    }
}

/**
 * Merge cached and fresh forecast slots by timestamp.
 * Fresh slots win on conflict. If fresh is empty, cached history is preserved.
 * Returns slots sorted ascending by time.
 */
function mergeForecastSlots(cached, fresh) {
    if (!fresh.length) return cached; // NWS outage — keep history untouched
    const tsOf = s => s.timestamp instanceof Date ? s.timestamp.getTime() : new Date(s.timestamp).getTime();
    const map = new Map();
    cached.forEach(s => map.set(tsOf(s), s));
    fresh.forEach(s => map.set(tsOf(s), s));   // fresh overwrites cached on conflict
    return Array.from(map.values()).sort((a, b) => tsOf(a) - tsOf(b));
}

/**
 * Load cached slots, merge with fresh, save, and return the full merged array.
 */
function updateForecastCache(locationKey, freshSlots) {
    const cached = loadForecastCache(locationKey);
    const merged = mergeForecastSlots(cached, freshSlots);
    if (merged.length) saveForecastCache(locationKey, merged);
    return merged;
}

/**
 * Fly to a location pin and open its sidebar — what tapping the pin does.
 * On desktop the view shifts left of the pin so the sidebar doesn't cover it.
 * @param {Object} item - conditions item from allConditionsData
 */
function focusLocationOnMap(item) {
    const isDesktop = window.innerWidth >= 768;
    const mapWidth = fishingMap.getSize().x;
    const offsetLng = isDesktop ? (mapWidth * 0.22) * (360 / Math.pow(2, 17 + 8)) : 0;

    fishingMap.flyTo([item.location.lat, item.location.lon - offsetLng], 17, {
        duration: 0.5
    });

    openLocationSidebar(item, item.key);
}

/* ==========================================================================
   TOP SPOT CARD — signed-in users, once a day, scoped to their saved filters
   ========================================================================== */

/**
 * Show "Best spot today" over the map for a signed-in user, at most once a day.
 * Called after saved default filters are applied to freshly loaded conditions.
 */
function showTopSpotCard() {
    if (typeof AUTH_CLIENT === 'undefined' || !AUTH_CLIENT.isAuthenticated() || !AUTH_CLIENT.currentUser) return;
    if (!document.body.classList.contains('view-map')) return;
    if (document.getElementById('topSpotCard')) return;

    const mapEl = document.getElementById('map');
    if (!mapEl || !fishingMap || !window.allConditionsData) return; // View spot needs a live map

    const scope = topSpotScope(typeof AUTH_UI !== 'undefined' ? AUTH_UI.preferences?.defaultFilters : null);
    const item = pickTopSpot(window.allConditionsData, scope);
    if (!item) return;

    const storageKey = 'rt.topSpotShown.' + AUTH_CLIENT.currentUser.id;
    const today = chicagoDateKey(new Date());
    try {
        if (localStorage.getItem(storageKey) === today) return;
        localStorage.setItem(storageKey, today);
    } catch (e) {
        // Storage unavailable: fall back to once per page load
        if (window._topSpotShownThisLoad) return;
        window._topSpotShownThisLoad = true;
    }

    mapEl.appendChild(buildTopSpotCard(item, scope));
}

function removeTopSpotCard() {
    const card = document.getElementById('topSpotCard');
    if (card) card.remove();
}

/**
 * Build the card with DOM nodes (names come from data, never parsed as HTML).
 */
function buildTopSpotCard(item, scope) {
    const score = item.conditions.score;
    const card = document.createElement('div');
    card.id = 'topSpotCard';
    card.className = 'top-spot-card';
    card.setAttribute('role', 'status');

    const badge = document.createElement('div');
    badge.className = 'top-spot-card__score';
    if (score >= 70) badge.style.borderColor = getScoreColor(score);
    const number = document.createElement('span');
    number.className = 'top-spot-card__number';
    number.textContent = score;
    const rating = document.createElement('span');
    rating.className = 'top-spot-card__rating';
    rating.textContent = getScoreLabel(score);
    badge.append(number, rating);

    const text = document.createElement('div');
    text.className = 'top-spot-card__text';
    const name = document.createElement('span');
    name.className = 'top-spot-card__name';
    name.textContent = item.location.name;
    const where = document.createElement('span');
    where.className = 'top-spot-card__where';
    where.textContent = scope.label ? 'Best spot today in ' + scope.label : 'Best spot today';
    text.append(name, where);

    const view = document.createElement('button');
    view.type = 'button';
    view.className = 'top-spot-card__view';
    view.textContent = 'View spot';
    view.addEventListener('click', function() {
        removeTopSpotCard();
        focusLocationOnMap(item);
    });

    const close = document.createElement('button');
    close.type = 'button';
    close.className = 'top-spot-card__close';
    close.setAttribute('aria-label', 'Dismiss');
    close.textContent = '×';
    close.addEventListener('click', removeTopSpotCard);

    card.append(badge, text, view, close);
    return card;
}

/**
 * Open the map sidebar for a fishing location item.
 * @param {Object} item - conditions item from allConditionsData
 * @param {string} locationKey
 */
function openLocationSidebar(item, locationKey) {
    const { sidebar, sidebarContent } = getSidebarElements();
    if (!sidebar || !sidebarContent) return;
    removeTopSpotCard(); // the card points at a spot; once any spot is open it has done its job
    closeLayersPanel();
    const popupHTML = buildMapPopupHTML(item.conditions, item.location, item.weatherData, item.tideData24h, locationKey);
    sidebarContent.innerHTML = popupHTML;
    sidebar.scrollTop = 0;
    sidebarContent.scrollTop = 0;
    sidebar.classList.add('open');
    // An errored My Spot has no conditions, so its sidebar has no chart to fill; skip the
    // detail fetch rather than fetch for a canvas that is not there.
    if (!item.conditions) return;
    // Defer one frame so the browser computes layout before we read canvas dimensions
    requestAnimationFrame(() => loadAndDrawSidebarChart(item, locationKey));
}

/**
 * Load detail data if needed, then draw the sidebar mini chart.
 * Cancels any previous in-flight fetch on each call.
 * Does NOT write to window.fishingForecast.
 * @param {Object} item
 * @param {string} locationKey
 */
async function loadAndDrawSidebarChart(item, locationKey) {
    const currentFBIScore = Number.isFinite(item.conditions?.score)
        ? item.conditions.score
        : null;

    if (_sidebarDetailController) {
        _sidebarDetailController.abort();
    }

    function getCanvas() {
        const { sidebarContent } = getSidebarElements();
        return sidebarContent ? sidebarContent.querySelector('.sidebar-mini-chart') : null;
    }

    // Fast path: data already enriched
    if (item.tideData24h || item._detailLoading) {
        if (item._detailLoading) return;
        const canvas = getCanvas();
        if (!canvas) return;
        if (typeof drawSidebarMiniChart !== 'function') return;
        const localForecast = computeLocalForecast(item.weatherData?._hourlyPeriods, item.tideData24h);
        const mergedForecast = updateForecastCache(locationKey, localForecast);
        drawSidebarMiniChart(canvas, item.tideData24h, mergedForecast, currentFBIScore);
        return;
    }

    // Slow path: fetch detail data
    item._detailLoading = true;
    _sidebarDetailController = new AbortController();
    const timeoutId = setTimeout(() => _sidebarDetailController.abort(), APP_TIMING.REC_FETCH_TIMEOUT_MS);

    try {
        const spotId = typeof getSpotIdFromKey === 'function' ? getSpotIdFromKey(locationKey) : null;
        const detailUrl = spotId
            ? API_CLIENT.baseURL + '/spots/' + spotId + '/detail'
            : API_CLIENT.baseURL + '/location/' + locationKey + '/detail';

        const response = await AUTH_CLIENT.fetchWithAuth(detailUrl, {
            signal: _sidebarDetailController.signal
        });
        clearTimeout(timeoutId);

        if (!response.ok) throw new Error('HTTP ' + response.status);
        const data = await response.json();
        if (!data.success) throw new Error(data.error || 'Failed');

        if (!item.weatherData) item.weatherData = {};
        item.weatherData._hourlyPeriods = data.detail.hourlyPeriods;
        item.tideData24h = data.detail.tideData24h;
        if (data.detail.sunriseSunsetData) item.sunriseSunsetData = data.detail.sunriseSunsetData;

        const canvas = getCanvas();
        if (!canvas) return;
        if (typeof drawSidebarMiniChart !== 'function') return;

        const localForecast = computeLocalForecast(item.weatherData._hourlyPeriods, item.tideData24h);
        const mergedForecast = updateForecastCache(locationKey, localForecast);
        drawSidebarMiniChart(canvas, item.tideData24h, mergedForecast, currentFBIScore);

    } catch (err) {
        clearTimeout(timeoutId);
        if (err.name === 'AbortError') return;
        console.warn('[Sidebar] Detail fetch failed:', err.message);
        const canvas = getCanvas();
        if (!canvas) return;
        if (typeof drawSidebarMiniChart !== 'function') return;
        // On fetch error: pass cached history so the chart still shows what it can
        const cachedForecast = loadForecastCache(locationKey);
        drawSidebarMiniChart(canvas, null, cachedForecast, currentFBIScore);
    } finally {
        item._detailLoading = false;
    }
}

/**
 * Lazy-create a base tile layer on first use (avoids loading satellite tiles on init)
 */
function ensureBaseLayer(name) {
    if (!baseLayers[name]) {
        const layerConfig = resolveLayerConfig(name);
        baseLayers[name] = L.tileLayer(layerConfig.url, {
            attribution: layerConfig.attribution,
            maxZoom: 19,
            tileSize: layerConfig.tileSize || 256,
            zoomOffset: layerConfig.zoomOffset || 0,
            updateWhenZooming: false,  // don't fetch tiles during zoom animation
            updateWhenIdle: true,      // fetch only after movement stops
            keepBuffer: 3,             // keep 3 tiles outside viewport (smoother panning)
            crossOrigin: true,         // enable CORS for SW caching
            // Resolves {key} url placeholders (Leaflet substitutes any named option into the
            // template). Harmless no-op for layers whose url has no {key} placeholder.
            key: getMaptilerKey()
        });
    }
    return baseLayers[name];
}

/**
 * Resolves a layer id to its tile config. 'voyager' normally points at MapTiler Basic
 * (see CONFIG.MAP_LAYERS.voyager), but that requires a key — falls back to the keyless
 * CONFIG.MAP_DEFAULTS (CyclOSM) when no key has loaded yet or one was withheld near the
 * monthly quota, so the base map (loaded on every session, unlike opt-in satellite) never
 * requests tiles with an empty ?key=. Unknown/removed layer ids (e.g. a stale persisted
 * 'satellite' preference after a provider is dropped) also fall back to this resolved voyager.
 */
function resolveLayerConfig(name) {
    if ((name === 'voyager' || !CONFIG.MAP_LAYERS[name]) && !hasMaptilerKey()) {
        return CONFIG.MAP_DEFAULTS;
    }
    return CONFIG.MAP_LAYERS[name] || CONFIG.MAP_LAYERS.voyager;
}

function getCachedIcon(type, size) {
    const key = `${type}_${size}`;
    if (!iconCache.has(key)) {
        const iconUrl = type === 'baitshop' ? './img/bait-shop.png'
                      : type === 'myspot' ? './img/top-pin-45.png'
                      : './img/normal-pin-45.png';
        // Normal/myspot pins are circular targets, so the coordinate is the circle's
        // center [size/2,size/2]. Bait shop is a building icon resting at ground level,
        // so it anchors at center-bottom [size/2,size] instead.
        const anchor = type === 'baitshop' ? [size / 2, size] : [size / 2, size / 2];
        const popupAnchor = type === 'baitshop' ? [0, -size] : [0, -size / 2];
        const opts = {
            iconUrl,
            iconSize: [size, size],
            iconAnchor: anchor,
            popupAnchor
        };
        iconCache.set(key, L.icon(opts));
    }
    return iconCache.get(key);
}

// Icon shown when this layer is the NEXT one (what we're switching TO)
const LAYER_ICONS = { voyager: 'street-view', satellite: 'satelite-view' };

/**
 * '' until GET /api/auth/config has returned a maptilerKey. Server withholds it near the
 * monthly MapTiler quota (see routes/auth.js) — checked before ever requesting a MapTiler-backed
 * layer (voyager or satellite) so a withheld key can't result in broken tile requests with an
 * empty {key}.
 */
function getMaptilerKey() {
    return (typeof AUTH_CLIENT !== 'undefined' && AUTH_CLIENT.config && AUTH_CLIENT.config.maptilerKey) || '';
}

function hasMaptilerKey() {
    return !!getMaptilerKey();
}

function getLayerCycle() {
    return hasMaptilerKey() ? ['voyager', 'satellite'] : ['voyager'];
}

function getNextLayer(current) {
    const cycle = getLayerCycle();
    const idx = cycle.indexOf(current);
    return cycle[(idx + 1) % cycle.length];
}

/**
 * Fire-and-forget usage beacon for the admin dashboard's MapTiler quota tracking (approximates
 * MapTiler's own "session" count — not their authoritative billing number). sendBeacon never
 * blocks or delays the layer switch itself; falls back to a non-awaited fetch if unsupported.
 */
function trackMaptilerSession() {
    const url = API_CLIENT.baseURL + '/map/maptiler-session';
    if (navigator.sendBeacon) {
        navigator.sendBeacon(url);
    } else {
        fetch(url, { method: 'POST', keepalive: true }).catch(function () {});
    }
}

/**
 * Core base-layer switch: creates the layer if needed, swaps it on the map, updates the
 * overlay blend mode, and pings the MapTiler usage beacon when satellite becomes active.
 * Shared by all three switch entry points (both toggle buttons + settings sync) so the
 * tracking call only needs to live in one place.
 */
function switchBaseLayer(nextLayer) {
    ensureBaseLayer(nextLayer); // Lazy-create on first switch
    fishingMap.removeLayer(baseLayers[currentLayer]);
    currentLayer = nextLayer;
    fishingMap.addLayer(baseLayers[currentLayer]);
    if (typeof updateOverlayBlendMode === 'function') updateOverlayBlendMode(currentLayer);
    // voyager now also hits MapTiler (Basic style) when a key is available, so it counts
    // toward the quota beacon too — only satellite used to be tracked here.
    if (nextLayer === 'satellite' || (nextLayer === 'voyager' && hasMaptilerKey())) trackMaptilerSession();
    // sendInteractionBeacon lives in main-server.js — defensive check in case map.js
    // is ever bundled standalone. Satellite has its own quota-tracking beacon above;
    // this is the "was switching to MapTiler worth it" comparison signal (Decision #2).
    if (nextLayer === 'voyager' && typeof sendInteractionBeacon === 'function') {
        sendInteractionBeacon('map_layer_voyager', {});
    }
}

/**
 * Prewarm tiles for the TX Gulf Coast bbox by injecting <link rel="prefetch"> tags.
 * Called once on first map init so tiles start downloading before Leaflet requests them.
 */
let _tilesPrewarmed = false;
function prewarmTiles() {
    if (_tilesPrewarmed) return;
    _tilesPrewarmed = true;

    const layerConfig = resolveLayerConfig(currentLayer);
    const tileUrl = layerConfig.url.replace('{key}', getMaptilerKey());
    const tileSize = layerConfig.tileSize || 256;
    const zoomOffset = layerConfig.zoomOffset || 0;

    // TX Gulf Coast bbox
    const bounds = { minLat: 26.0, maxLat: 30.0, minLon: -97.5, maxLon: -93.5 };
    const zoom = 9 + zoomOffset; // effective tile zoom for the default view

    function latLonToTile(lat, lon, z) {
        const x = Math.floor((lon + 180) / 360 * Math.pow(2, z));
        const y = Math.floor((1 - Math.log(Math.tan(lat * Math.PI / 180) +
            1 / Math.cos(lat * Math.PI / 180)) / Math.PI) / 2 * Math.pow(2, z));
        return { x, y };
    }

    const topLeft = latLonToTile(bounds.maxLat, bounds.minLon, zoom);
    const bottomRight = latLonToTile(bounds.minLat, bounds.maxLon, zoom);

    for (let x = topLeft.x; x <= bottomRight.x; x++) {
        for (let y = topLeft.y; y <= bottomRight.y; y++) {
            const url = tileUrl
                .replace('{s}', 'a')
                .replace('{z}', zoom)
                .replace('{x}', x)
                .replace('{y}', y);
            const link = document.createElement('link');
            link.rel = 'prefetch';
            link.href = url;
            link.as = 'image';
            document.head.appendChild(link);
        }
    }
}

// --- Progressive loading: skeleton pins + incremental marker rendering ---
let skeletonMarkerGroup = null;
const skeletonMarkers = new Map(); // key -> L.marker

/**
 * Show gray placeholder pins at all known locations (before conditions data arrives).
 * Gives immediate spatial context while SSE data loads; each skeleton is replaced
 * one-by-one via progressiveAddMarker() as scores arrive.
 */
function showSkeletonPins(locationMeta) {
    if (!fishingMap) return;

    skeletonMarkerGroup = L.featureGroup();
    const pinSize = getPinSizeForZoom(fishingMap.getZoom());

    locationMeta.forEach(loc => {
        if (!isFinite(loc.lat) || !isFinite(loc.lon)) return;
        const marker = L.marker([loc.lat, loc.lon], {
            icon: getCachedIcon('normal', pinSize),
            opacity: 0.3
        }).addTo(skeletonMarkerGroup);
        skeletonMarkers.set(loc.key, marker);
    });

    skeletonMarkerGroup.addTo(fishingMap);
}

/**
 * Replace a skeleton pin with a real scored pin as conditions data arrives
 */
function progressiveAddMarker(item, allConditionsSoFar) {
    if (!fishingMap || !item.location) return;
    if (!isFinite(item.location.lat) || !isFinite(item.location.lon)) return;

    // Remove skeleton pin for this location
    const skelMarker = skeletonMarkers.get(item.key);
    if (skelMarker && skeletonMarkerGroup) {
        skeletonMarkerGroup.removeLayer(skelMarker);
        skeletonMarkers.delete(item.key);
    }

    // Create real marker group if not exists
    if (!markerGroup) {
        markerGroup = L.featureGroup();
        markerGroup.addTo(fishingMap);
    }

    const pinSize = getPinSizeForZoom(fishingMap.getZoom());
    const currentRank = allConditionsSoFar.findIndex(c => c.key === item.key);
    const pinType = item._isMySpot ? 'myspot' : 'normal';

    const marker = L.marker([item.location.lat, item.location.lon], {
        icon: getCachedIcon(pinType, pinSize)
    }).addTo(markerGroup);

    mapMarkers.push({ marker, pinType });

    // Attach click handler (same as showMapWithLocationsByRegion)
    marker.on('click', function(e) {
        L.DomEvent.stopPropagation(e);
        focusLocationOnMap(item);
    });
}

/**
 * Remove any remaining skeleton pins after all data has loaded
 */
function removeSkeletonPins() {
    if (skeletonMarkerGroup && fishingMap) {
        fishingMap.removeLayer(skeletonMarkerGroup);
    }
    skeletonMarkerGroup = null;
    skeletonMarkers.clear();
}

// Local storage key for map settings
const MAP_SETTINGS_KEY = 'reelTalk_mapSettings';

/**
 * Load map settings from localStorage
 */
function loadMapSettings() {
    try {
        const saved = localStorage.getItem(MAP_SETTINGS_KEY);
        if (saved) {
            const parsed = JSON.parse(saved);
            if (!parsed.overlays) parsed.overlays = {};
            return parsed;
        }
    } catch (e) {
        console.error('Error loading map settings:', e);
    }
    return { layer: 'voyager', showBaitShops: false, overlays: {} };
}

/**
 * Save map settings to localStorage. An overlay the current tier can't use keeps its
 * saved value, so a layer enforceOverlayTier() hid comes back after re-subscribing.
 */
function saveMapSettings() {
    try {
        const overlays = typeof getOverlayState === 'function' ? getOverlayState() : {};
        const saved = loadMapSettings().overlays;
        if (typeof OVERLAY_CONFIG !== 'undefined') {
            OVERLAY_CONFIG.forEach(config => {
                if (!canUseOverlay(config)) overlays[config.id] = !!saved[config.id];
            });
        }
        const settings = {
            layer: currentLayer,
            showBaitShops: baitShopsVisible,
            overlays
        };
        localStorage.setItem(MAP_SETTINGS_KEY, JSON.stringify(settings));
    } catch (e) {
        console.error('Error saving map settings:', e);
    }
}

/**
 * Sync the live map to match current localStorage settings.
 * Called after saving preferences in Settings so the Spot page reflects changes immediately.
 */
function syncMapLayersFromSettings() {
    if (!fishingMap) return;

    const saved = loadMapSettings();

    // ── Base layer ──
    const savedLayerAvailable = CONFIG.MAP_LAYERS[saved.layer]
        && (saved.layer !== 'satellite' || hasMaptilerKey());
    if (saved.layer !== currentLayer && savedLayerAvailable) {
        switchBaseLayer(saved.layer);

        const layerBtn = document.getElementById('mapLayerBtn');
        if (layerBtn) {
            const upcomingLayer = getNextLayer(currentLayer);
            layerBtn.innerHTML = `<img src="./img/${LAYER_ICONS[upcomingLayer]}.png" alt="Switch to ${upcomingLayer}" />`;
        }
    }

    // ── Bait shops ──
    if (saved.showBaitShops !== baitShopsVisible) {
        if (saved.showBaitShops) {
            const region = document.getElementById('popupRegionSelect')?.value || 'all';
            const state = document.getElementById('popupStateSelect')?.value || 'all';
            showBaitShops(region, state);
        } else {
            hideBaitShops();
        }
    }
    const baitShopCb = document.getElementById('mapLayersBaitShopsCheckbox');
    if (baitShopCb) baitShopCb.checked = !!saved.showBaitShops;

    // ── Overlays (contour layers) ──
    if (saved.overlays && typeof setOverlayVisible === 'function') {
        Object.entries(saved.overlays).forEach(([id, visible]) => setOverlayVisible(id, visible));
        // Checked state follows the real result: toggleOverlay() refuses Pro layers for non-Pro users
        const state = getOverlayState();
        document.querySelectorAll('#mapLayersPanel input[data-overlay-id]').forEach(cb => {
            cb.checked = !!state[cb.dataset.overlayId];
        });
    }

    console.log('[Map] Synced layers from settings');
}

// Base pin size and zoom-based scaling
const PIN_BASE_SIZE = 45;
const PIN_MIN_SCALE = 0.25; // 25% at min zoom
const PIN_MAX_SCALE = 1.1; // 110% at max zoom

// Bait shop marker size and scaling
const BAIT_SHOP_BASE_SIZE = 32;
const BAIT_SHOP_MIN_SCALE = 0.5; // 50% at min zoom
const BAIT_SHOP_MAX_SCALE = 1.2; // 120% at max zoom

/**
 * Calculate pin size based on current zoom level
 * Scales linearly from PIN_MIN_SCALE to PIN_MAX_SCALE
 */
function getPinSizeForZoom(zoom) {
    const minZoom = 8;
    const maxZoom = 18;
    const clampedZoom = Math.max(minZoom, Math.min(maxZoom, zoom));
    const t = (clampedZoom - minZoom) / (maxZoom - minZoom);
    const scale = PIN_MIN_SCALE + (PIN_MAX_SCALE - PIN_MIN_SCALE) * t;
    return Math.round(PIN_BASE_SIZE * scale);
}

/**
 * Calculate bait shop marker size based on current zoom level
 */
function getBaitShopSizeForZoom(zoom) {
    const minZoom = 8;
    const maxZoom = 18;
    const clampedZoom = Math.max(minZoom, Math.min(maxZoom, zoom));
    const t = (clampedZoom - minZoom) / (maxZoom - minZoom);
    const scale = BAIT_SHOP_MIN_SCALE + (BAIT_SHOP_MAX_SCALE - BAIT_SHOP_MIN_SCALE) * t;
    return Math.round(BAIT_SHOP_BASE_SIZE * scale);
}

/**
 * Update all marker icons based on current zoom level
 */
function updateMarkerSizes() {
    if (!fishingMap) return;

    const zoom = fishingMap.getZoom();

    // Update fishing spot markers (skip if size unchanged)
    if (mapMarkers.length > 0) {
        const pinSize = getPinSizeForZoom(zoom);
        if (pinSize !== lastZoomPinSize) {
            mapMarkers.forEach(({ marker, pinType }) => {
                marker.setIcon(getCachedIcon(pinType || 'normal', pinSize));
            });
            lastZoomPinSize = pinSize;
        }
    }

    // Update bait shop markers (skip if size unchanged)
    if (baitShopMarkers.length > 0) {
        const baitShopSize = getBaitShopSizeForZoom(zoom);
        if (baitShopSize !== lastZoomBaitShopSize) {
            baitShopMarkers.forEach(marker => {
                marker.setIcon(getCachedIcon('baitshop', baitShopSize));
            });
            lastZoomBaitShopSize = baitShopSize;
        }
    }
}

function resetMapView(e) {
    // Stop event from bubbling if called from button click
    if (e && e.stopPropagation) {
        e.stopPropagation();
    }
    if (fishingMap && mapDefaultBounds) {
        closeMapSidebar();
        // Cap padding to 1/3 of the smallest container dimension so the effective
        // size after Leaflet's double-subtraction (both sides) stays positive.
        // On small/landscape screens the map can be ~95px tall; 50+50=100 > 95
        // causes getBoundsZoom to return NaN → "Invalid LatLng object: (NaN, NaN)".
        const { x, y } = fishingMap.getSize();
        const safePad = Math.max(0, Math.min(50, Math.floor(Math.min(x, y) / 3)));
        fishingMap.flyToBounds(mapDefaultBounds, {
            padding: [safePad, safePad],
            duration: 0.5
        });
    }
}

/**
 * Update zoom reset button visibility based on current zoom level
 * Shows zoom-out button when user zooms in beyond the default bounds zoom level
 * Shows zoom-in button when user zooms out beyond the default bounds zoom level
 */
function updateZoomResetButtonVisibility() {
    const zoomResetBtn = document.getElementById('mapZoomResetBtn');
    const zoomResetInBtn = document.getElementById('mapZoomResetInBtn');
    if (!zoomResetBtn || !zoomResetInBtn || !fishingMap || !mapDefaultBounds) return;

    // Use cached zoom level (recomputed only when bounds change in showMapWithLocationsByRegion)
    if (_cachedDefaultZoom === null) {
        _cachedDefaultZoom = fishingMap.getBoundsZoom(mapDefaultBounds, false, [50, 50]);
    }
    const defaultZoom = _cachedDefaultZoom;
    const currentZoom = fishingMap.getZoom();

    // Show appropriate button based on zoom direction
    if (currentZoom > defaultZoom + 0.5) {
        // Zoomed in - show zoom-reset (zoom out to default)
        zoomResetBtn.style.display = 'flex';
        zoomResetInBtn.style.display = 'none';
    } else if (currentZoom < defaultZoom - 0.5) {
        // Zoomed out - show zoom-reset-in (zoom in to default)
        zoomResetBtn.style.display = 'none';
        zoomResetInBtn.style.display = 'flex';
    } else {
        // At default zoom - hide both
        zoomResetBtn.style.display = 'none';
        zoomResetInBtn.style.display = 'none';
    }
}

function closeMapSidebar() {
    const { sidebar, sidebarContent } = getSidebarElements();
    if (!sidebar) return;
    sidebar.classList.remove('open');
    sidebar.scrollTop = 0;
    if (sidebarContent) {
        sidebarContent.scrollTop = 0;
    }
}

/**
 * Initialize the map if not yet created. Returns true on fresh init, false if already existed.
 */
function ensureMapInitialized(initialBounds) {
    if (fishingMap) return false; // Already initialized

    // Load saved settings before creating map
    const savedSettings = loadMapSettings();
    const savedLayerAvailable = CONFIG.MAP_LAYERS[savedSettings.layer]
        && (savedSettings.layer !== 'satellite' || hasMaptilerKey());
    currentLayer = savedLayerAvailable ? savedSettings.layer : 'voyager';

    // Start tile prefetch for TX Gulf Coast bbox (downloads in background)
    prewarmTiles();

    // Create map instance — use provided bounds if available to avoid
    // loading tiles at a default view then immediately switching
    fishingMap = L.map('map', {
        zoomControl: false,
        attributionControl: false,
        preferCanvas: true, // use canvas renderer for vector layers (perf)
        minZoom: 6,
        maxZoom: 19
    });

    if (initialBounds) {
        fishingMap.fitBounds(initialBounds, { padding: [50, 50], animate: false });
    } else {
        fishingMap.setView(CONFIG.MAP_DEFAULTS.center, CONFIG.MAP_DEFAULTS.zoom);
    }

    // Initialize layers and controls
    initializeMapLayers();

    // Bait shop restore is handled in createMapLegend() with proper region/state filters

    // Click on map area closes sidebar if open
    fishingMap.on('click', function(e) {
        const target = e.originalEvent.target;
        // Ignore clicks on control buttons, the Layers panel and the sidebar (its images would
        // otherwise match the IMG check below and close it)
        if (target.closest('.map-controls-bottom, .map-controls-top, #mapSidebar')) {
            return;
        }
        // Close sidebar if clicking on map tiles/container
        if (target.classList.contains('leaflet-container') ||
            target.classList.contains('leaflet-tile') ||
            target.classList.contains('leaflet-tile-pane') ||
            target.tagName === 'IMG') {
            closeMapSidebar();
        }
    });

    // Sidebar interaction handlers
    const sidebar = document.getElementById('mapSidebar');
    // The sidebar lives inside #map, so a touch swipe would pan the map instead of scrolling
    // the sidebar (Leaflet cancels the touchmove). A hover toggle can't cover phones.
    isolateFromMapGestures(sidebar);
    sidebar.addEventListener('click', function(e) {
        if (e.target.closest('.sidebar-title-close-btn')) {
            e.stopPropagation();
            closeMapSidebar();
            resetMapView();
            return;
        }
        const btn = e.target.closest('.quick-start-trip-btn');
        if (!btn) return;
        e.stopPropagation();
        if (typeof JOURNAL !== 'undefined' && typeof JOURNAL.quickStartTrip === 'function') {
            JOURNAL.quickStartTrip(
                btn.dataset.locationName,
                btn.dataset.locationKey || null,
                btn.dataset.lat || null,
                btn.dataset.lon || null
            );
        }
    });

    // Dynamic pin sizing on zoom and zoom reset button visibility
    fishingMap.on('zoomend', function() {
        updateMarkerSizes();
        updateZoomResetButtonVisibility();
        const zoomEl = document.getElementById('mapZoomIndicator');
        if (zoomEl) zoomEl.textContent = 'z' + fishingMap.getZoom();
    });

    return true; // Fresh init
}

/**
 * Clear fishing spot markers from the map (preserves bait shop markers)
 */
function clearMapMarkers() {
    if (!fishingMap) return;

    // Remove fishing spot marker group in one operation (preserves bait shop markers)
    if (markerGroup) {
        fishingMap.removeLayer(markerGroup);
        markerGroup = null;
    }

    // Clear stored marker references and reset zoom tracking
    mapMarkers = [];
    lastZoomPinSize = null;
}

// Function to show all spots filtered by region, state, and count (for dropdowns)
function showMapWithLocationsByRegion(allConditions, selectedRegion = 'all', selectedCount = 'all', selectedState = 'all') {
    // Clear existing markers (safe no-op if map not yet initialized)
    clearMapMarkers();

    // Filter locations by selected state first
    let filteredLocations = selectedState === 'all'
        ? allConditions
        : allConditions.filter(item => item.location.state === selectedState);

    // Then filter by selected region (always include user spots)
    if (selectedRegion !== 'all') {
        filteredLocations = filteredLocations.filter(item => item._isMySpot || item.location.region === selectedRegion);
    }

    // Limit by count if specified
    if (selectedCount !== 'all') {
        const limit = parseInt(selectedCount, 10);
        filteredLocations = filteredLocations.slice(0, limit);
    }

    // Drop entries with missing/invalid coordinates
    filteredLocations = filteredLocations.filter(item =>
        item.location && isFinite(item.location.lat) && isFinite(item.location.lon)
    );

    // Safety check: ensure we have locations before creating bounds
    if (filteredLocations.length === 0) {
        console.error('No locations available to display on map for region:', selectedRegion);
        return;
    }

    const bounds = L.latLngBounds(filteredLocations.map(item => [item.location.lat, item.location.lon]));
    mapDefaultBounds = bounds;
    _cachedDefaultZoom = null; // Reset cached zoom when bounds change

    // Initialize map at the correct bounds (first call) or adjust view (subsequent calls)
    // Passing bounds into init avoids loading tiles at a default view then immediately switching
    const wasNewInit = ensureMapInitialized(bounds);
    if (!wasNewInit) {
        // Only call fitBounds again when map already existed (bounds change from filter)
        fishingMap.fitBounds(bounds, {
            padding: [50, 50],
            animate: false
        });
    } else {
        // Fresh init: container may not have final dimensions yet (e.g. login flow
        // just unhid the tab container). Give the browser a frame to lay out, then
        // recalculate tile positions and re-fit bounds.
        requestAnimationFrame(function() {
            fishingMap.invalidateSize();
            fishingMap.fitBounds(bounds, { padding: [50, 50], animate: false });
        });
    }

    // Create feature group OFF-map, add all markers, then attach once (single render)
    markerGroup = L.featureGroup();

    const pinSize = getPinSizeForZoom(fishingMap.getZoom());
    lastZoomPinSize = pinSize;

    filteredLocations.forEach((item, index) => {
        const pinType = item._isMySpot ? 'myspot' : 'normal';
        const markerIcon = getCachedIcon(pinType, pinSize);

        const marker = L.marker([item.location.lat, item.location.lon], {
            icon: markerIcon
        }).addTo(markerGroup); // Adds to group (not yet on map)

        mapMarkers.push({ marker, pinType });

        marker.on('click', function(e) {
            L.DomEvent.stopPropagation(e);
            focusLocationOnMap(item);
        });
    });

    // Single DOM operation: all markers appear at once
    markerGroup.addTo(fishingMap);
}

/**
 * Initialize map base layers and create toggle button
 */
function initializeMapLayers() {
    if (!fishingMap) return;

    // Create only the default layer; the other is lazy-created on first toggle
    ensureBaseLayer(currentLayer);
    baseLayers[currentLayer].addTo(fishingMap);
    // Doesn't go through switchBaseLayer() (no prior layer to remove), so track separately —
    // otherwise a fresh page load would never register a session, undercounting the layer
    // that's actually active on nearly every session now that voyager is MapTiler-backed.
    if (currentLayer === 'satellite' || (currentLayer === 'voyager' && hasMaptilerKey())) trackMaptilerSession();

    // Create map control buttons (Setup, Filter, Jump)
    createMapControlButtons();

    // Create map legend
    createMapLegend();
}

/**
 * Create map legend showing marker types (below the map), plus a hidden colour key for
 * each overlay with `classes` (Bed Type). toggleOverlay() shows and hides the key; it
 * starts from the live state because the saved overlays are restored before this runs.
 * Bait Shop and overlay toggles live in the Layers panel (createLayersControl()).
 */
function createMapLegend() {
    // Check if legend already exists
    if (document.querySelector('.map-legend')) {
        return;
    }

    const mapContainer = document.getElementById('mapContainer');

    const legend = document.createElement('div');
    legend.className = 'map-legend';

    legend.innerHTML = `
        <div class="legend-items">
            <div class="legend-group-left">
                <div class="legend-item">
                    <img src="./img/normal-pin-45.png" alt="Public" class="legend-icon">
                    <span>Public</span>
                </div>
                <div class="legend-item">
                    <img src="./img/top-pin-45.png" alt="Private" class="legend-icon">
                    <span>Private</span>
                </div>
            </div>
            ${buildClassLegendsHTML()}
        </div>
    `;

    // Insert after map container
    mapContainer.insertAdjacentElement('afterend', legend);
}

/**
 * Colour keys for every overlay with `classes`, each hidden unless its overlay is on.
 * Swatches use the overlay's fill opacity so they match the map.
 * @returns {string} HTML
 */
function buildClassLegendsHTML() {
    if (typeof OVERLAY_CONFIG === 'undefined') return '';
    const state = getOverlayState();
    return OVERLAY_CONFIG.filter(config => config.classes).map(config => {
        const swatches = Object.values(config.classes).map(cls => `<span class="legend-class">
                    <span class="legend-class-color" style="background:${cls.color};opacity:${config.opacity}"></span>${cls.name}
                </span>`).join('');
        const note = config.legendNote ? `<span class="legend-class-note">${config.legendNote}</span>` : '';
        return `<div class="legend-classes" data-legend-for="${config.id}"${state[config.id] ? '' : ' hidden'}>${swatches}${note}</div>`;
    }).join('');
}

/**
 * Update a layer-cycle button's icon to show what layer it will switch TO next. Shared by
 * the mobile cycle button (click + init) and the desktop layer <select>'s change handler,
 * so #headerLayerBtn's icon doesn't go stale regardless of which control last changed the
 * layer (e.g. picking a layer from the desktop select, then resizing to mobile mid-session).
 */
function updateLayerButtonIcon(btn) {
    if (!btn) return;
    const upcomingLayer = getNextLayer(currentLayer);
    btn.innerHTML = `<img src="./img/${LAYER_ICONS[upcomingLayer]}.png" alt="Switch to ${upcomingLayer}" />`;
}

/**
 * Toggle map layer and update the control button icon
 */
function toggleMapLayerWithButton(e) {
    // Stop event from bubbling to map click handler
    if (e) {
        e.stopPropagation();
    }

    if (!fishingMap) return;

    const nextLayer = getNextLayer(currentLayer);
    switchBaseLayer(nextLayer);

    updateLayerButtonIcon(document.getElementById('headerLayerBtn'));

    // Save layer preference
    saveMapSettings();
}

// ==================== Map Control Buttons ====================

let baitShopMarkers = []; // Store bait shop marker references
let baitShopsVisible = false;
let baitShopsData = null;   // Cached permanently after first fetch — bait shop data is static
let currentBaitShopRegion = 'all'; // Track current region filter for bait shops
let currentBaitShopState = 'all'; // Track current state filter for bait shops

/**
 * Create map control buttons (Setup, Filter, Jump)
 */
function createMapControlButtons() {
    // Check if controls already exist
    if (document.querySelector('.map-controls-bottom')) {
        return;
    }

    const mapElement = document.getElementById('map');

    // Update the header layer button icon to reflect saved/current layer preference
    updateLayerButtonIcon(document.getElementById('headerLayerBtn'));

    // Bottom-right controls: zoom reset (hidden by default) stacked above locate
    const bottomControlsContainer = document.createElement('div');
    bottomControlsContainer.className = 'map-controls-bottom';

    // Zoom reset button (hidden by default, shows when zoomed in)
    const zoomResetBtn = document.createElement('button');
    zoomResetBtn.className = 'map-control-btn zoom-reset-btn';
    zoomResetBtn.id = 'mapZoomResetBtn';
    zoomResetBtn.innerHTML = '<img src="./img/zoom-reset.png" alt="Reset zoom" />';
    zoomResetBtn.onclick = resetMapView;
    zoomResetBtn.style.display = 'none'; // Hidden by default

    // Zoom reset button for zoomed out (hidden by default, shows when zoomed out)
    const zoomResetInBtn = document.createElement('button');
    zoomResetInBtn.className = 'map-control-btn zoom-reset-btn';
    zoomResetInBtn.id = 'mapZoomResetInBtn';
    zoomResetInBtn.innerHTML = '<img src="./img/zoom-reset-in.png" alt="Reset zoom" />';
    zoomResetInBtn.onclick = resetMapView;
    zoomResetInBtn.style.display = 'none'; // Hidden by default

    // Locate me button
    const locateBtn = document.createElement('button');
    locateBtn.className = 'map-control-btn';
    locateBtn.id = 'mapLocateBtn';
    locateBtn.innerHTML = '<img src="./img/locate-me.png" alt="Locate me" />';
    locateBtn.onclick = locateUser;

    bottomControlsContainer.appendChild(zoomResetBtn);
    bottomControlsContainer.appendChild(zoomResetInBtn);
    bottomControlsContainer.appendChild(locateBtn);
    mapElement.appendChild(bottomControlsContainer);

    // Admin-only zoom level indicator
    if (typeof AUTH_CLIENT !== 'undefined' &&
        AUTH_CLIENT.currentUser && AUTH_CLIENT.currentUser.role === 'admin') {
        const zoomEl = document.createElement('div');
        zoomEl.id = 'mapZoomIndicator';
        zoomEl.textContent = 'z' + (fishingMap ? fishingMap.getZoom() : '–');
        zoomEl.style.cssText = [
            'position:absolute', 'top:8px', 'left:8px', 'z-index:1000',
            'background:rgba(0,0,0,0.55)', 'color:#fff',
            'font-size:12px', 'font-family:monospace', 'font-weight:600',
            'padding:2px 6px', 'border-radius:4px', 'pointer-events:none',
            'letter-spacing:0.5px'
        ].join(';');
        mapElement.appendChild(zoomEl);
    }

    // Create setup popup (hidden by default)
    createSetupPopup(mapElement);

    // One-time wiring for the desktop inline filter group + layer select
    initInlineControls();

    // Top-right Layers button + panel. After the setup popup: the bait shop restore reads
    // its region/state selects.
    createLayersControl(mapElement, loadMapSettings());

    // Add click-outside handler to close popup
    document.addEventListener('click', function(e) {
        const setupPopup = document.getElementById('setupPopup');

        // Close setup popup if clicking outside (filter buttons are in section headers)
        if (setupPopup && setupPopup.classList.contains('open')) {
            const clickedFilter = e.target.closest('.jump-to-map-btn');
            if (!setupPopup.contains(e.target) && !clickedFilter) {
                closeSetupPopup();
            }
        }
    });

    // Escape closes the setup popup and returns focus to the filter button that opened it
    document.addEventListener('keydown', function(e) {
        const setupPopup = document.getElementById('setupPopup');
        if (e.key !== 'Escape' || !setupPopup || !setupPopup.classList.contains('open')) return;
        closeSetupPopup({ returnFocus: true });
    });
}

// Stacked-layers glyph for #mapLayersBtn, lock glyph for locked rows
const LAYERS_ICON_SVG = '<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
    '<polygon points="12 2 2 7 12 12 22 7 12 2"/><polyline points="2 17 12 22 22 17"/><polyline points="2 12 12 17 22 12"/></svg>';
const LOCK_ICON_SVG = '<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
    '<rect x="4" y="11" width="16" height="10" rx="2"/><path d="M8 11V7a4 4 0 0 1 8 0v4"/></svg>';

/**
 * Build the Layers panel rows: Bait Shop first (a marker set, not an OVERLAY_CONFIG
 * layer; free for every tier), then one row per OVERLAY_CONFIG entry. Layers the current
 * tier can't use render as locked [data-upgrade] buttons (no checkbox); the rest as
 * checkboxes whose checked state is the live state.
 * @returns {string} HTML
 */
function buildLayersPanelRowsHTML() {
    const state = getOverlayState();
    const baitShopRow = `<label class="legend-checkbox map-layers-row">
                <input type="checkbox" id="mapLayersBaitShopsCheckbox"${baitShopsVisible ? ' checked' : ''}>
                <span class="map-layers-name">Bait Shop</span>
            </label>`;
    return baitShopRow + OVERLAY_CONFIG.map(config => {
        if (!canUseOverlay(config)) {
            return `<button type="button" class="map-layers-row is-locked" data-upgrade data-feature="overlay_${config.id}">
                <span class="map-layers-name">${config.name}</span>
                <span class="map-layers-pro">${LOCK_ICON_SVG}Pro</span>
            </button>`;
        }
        return `<label class="legend-checkbox map-layers-row">
                <input type="checkbox" data-overlay-id="${config.id}"${state[config.id] ? ' checked' : ''}>
                <span class="map-layers-name">${config.name}</span>
            </label>`;
    }).join('');
}

/**
 * Create the top-right Layers button and its overlay checkbox panel, then restore saved
 * overlay states. toggleOverlay() refuses Pro layers for non-Pro users, so the restore
 * needs no tier check of its own.
 * @param {HTMLElement} mapElement - #map
 * @param {Object} savedSettings - from loadMapSettings()
 */
function createLayersControl(mapElement, savedSettings) {
    if (typeof initMapOverlays !== 'function' || document.querySelector('.map-controls-top')) return;

    initMapOverlays(fishingMap);
    // Set initial blend mode based on saved base layer
    if (typeof updateOverlayBlendMode === 'function') updateOverlayBlendMode(currentLayer);

    // Restore saved overlay states before building rows, so rows show the real result
    Object.entries(savedSettings.overlays || {}).forEach(([id, visible]) => {
        if (visible) setOverlayVisible(id, true);
    });

    const container = document.createElement('div');
    container.className = 'map-controls-top';
    container.innerHTML = `
        <button type="button" class="map-control-btn" id="mapLayersBtn" aria-label="Map layers"
                aria-expanded="false" aria-controls="mapLayersPanel">${LAYERS_ICON_SVG}</button>
        <div class="map-layers-panel" id="mapLayersPanel" hidden>
            <div class="map-layers-title" id="mapLayersTitle">Map layers</div>
            <div class="map-layers-rows" id="mapLayersRows" role="group" aria-labelledby="mapLayersTitle">
                ${buildLayersPanelRowsHTML()}
            </div>
        </div>
    `;
    mapElement.appendChild(container);

    isolateFromMapGestures(container);

    const btn = container.querySelector('#mapLayersBtn');
    const panel = container.querySelector('#mapLayersPanel');
    btn.addEventListener('click', function() {
        if (panel.hidden) {
            panel.hidden = false;
            btn.setAttribute('aria-expanded', 'true');
        } else {
            closeLayersPanel();
        }
    });

    // Delegated on the panel so rows rebuilt by refreshLayersPanel() need no rewiring
    panel.addEventListener('change', async function(e) {
        if (e.target.id === 'mapLayersBaitShopsCheckbox') {
            const selectedRegion = document.getElementById('popupRegionSelect')?.value || 'all';
            const selectedState = document.getElementById('popupStateSelect')?.value || 'all';
            if (e.target.checked) {
                await showBaitShops(selectedRegion, selectedState);
            } else {
                hideBaitShops();
            }
            saveMapSettings();
            return;
        }
        const cb = e.target.closest('input[data-overlay-id]');
        if (!cb) return;
        // Sync checkbox to the real result (layer creation can fail or be refused)
        cb.checked = toggleOverlay(cb.dataset.overlayId);
        saveMapSettings();
    });

    // Outside click closes; so does a locked row, whose [data-upgrade] click opens the
    // upgrade modal (the panel shouldn't stay open behind it)
    document.addEventListener('click', function(e) {
        if (!container.contains(e.target) || e.target.closest('.map-layers-row.is-locked')) closeLayersPanel();
    });

    // Focus goes back to the button only from inside the panel; another dialog owns it otherwise
    document.addEventListener('keydown', function(e) {
        if (e.key === 'Escape') closeLayersPanel({ returnFocus: container.contains(document.activeElement) });
    });

    // Auto-show bait shops if previously enabled (showBaitShops() ticks the row when loaded)
    if (savedSettings.showBaitShops) {
        const selectedRegion = document.getElementById('popupRegionSelect')?.value || 'all';
        const selectedState = document.getElementById('popupStateSelect')?.value || 'all';
        showBaitShops(selectedRegion, selectedState);
    }
}

/**
 * Close the Layers panel if open.
 * @param {Object} [options]
 * @param {boolean} [options.returnFocus] - move focus back to #mapLayersBtn
 */
function closeLayersPanel(options) {
    const panel = document.getElementById('mapLayersPanel');
    const btn = document.getElementById('mapLayersBtn');
    if (!panel || !btn || panel.hidden) return;
    panel.hidden = true;
    btn.setAttribute('aria-expanded', 'false');
    if (options && options.returnFocus) btn.focus();
}

/**
 * Re-apply the current tier to the Layers panel: hide overlays the tier no longer allows,
 * bring the live map in line with the saved settings (applyMapLayerDefaults() has just
 * written the account's defaults on sign-in; Pro layers refused at map init while the
 * tier was still free or guest turn on now), then rebuild the rows (locked <-> checkbox).
 * Called after sign-in, sign-out and Stripe return. Also re-locks the Settings overlay
 * defaults, which exist before the map does, keeping any unsaved ticks.
 */
function refreshLayersPanel() {
    if (typeof AUTH_UI !== 'undefined') AUTH_UI.renderSettingsOverlayOptions({ keepTicks: true });
    const rows = document.getElementById('mapLayersRows');
    if (!rows) return;
    enforceOverlayTier();
    // Base layer, bait shops and every overlay on or off; toggleOverlay() still refuses
    // what the tier can't use
    syncMapLayersFromSettings();
    rows.innerHTML = buildLayersPanelRowsHTML();
}

/**
 * Locate user using browser geolocation and show on map
 */
var _mapLocating = false;
function locateUser(e) {
    // Stop event from bubbling to map click handler
    if (e) e.stopPropagation();
    if (_mapLocating) return;

    if (!navigator.geolocation) {
        if (typeof showAppToast === 'function') showAppToast('Geolocation is not supported by your browser', 'error');
        return;
    }

    if (!fishingMap) {
        if (typeof showAppToast === 'function') showAppToast('Map is not initialized', 'error');
        return;
    }

    var btn = document.getElementById('mapLocateBtn');
    if (btn) btn.disabled = true;
    _mapLocating = true;

    getAccurateLocation({ desiredAccuracy: 30, maxWaitMs: 8000 }).then(
        function(position) {
            _mapLocating = false;
            if (btn) btn.disabled = false;
            var latitude = position.coords.latitude;
            var longitude = position.coords.longitude;

            // Remove existing user location marker if present
            if (userLocationMarker) {
                fishingMap.removeLayer(userLocationMarker);
            }

            // Create blue dot marker for user location
            var userIcon = L.divIcon({
                className: 'user-location-marker',
                html: '<div class="user-dot"></div>',
                iconSize: [20, 20],
                iconAnchor: [10, 10]
            });

            userLocationMarker = L.marker([latitude, longitude], { icon: userIcon })
                .addTo(fishingMap)
                .bindPopup('You are here');

            // Pan and zoom to user location
            fishingMap.setView([latitude, longitude], 13);

            if (position.coords.accuracy > 100 && typeof showAppToast === 'function') {
                showAppToast('Location may be approximate (~' + Math.round(position.coords.accuracy) + 'm)', 'info');
            }
        },
        function(error) {
            _mapLocating = false;
            if (btn) btn.disabled = false;
            if (typeof showAppToast === 'function') showAppToast(formatGeolocationError(error), 'error');
        }
    );
}

/**
 * Toggle setup popup visibility
 */
function toggleSetupPopup(e) {
    // Stop event from bubbling to map click handler
    if (e) e.stopPropagation();

    const popup = document.getElementById('setupPopup');
    const filterPopup = document.getElementById('filterPopup');

    // Close filter popup if open
    if (filterPopup) filterPopup.classList.remove('open');

    // Toggle setup popup
    if (popup) {
        const isOpening = !popup.classList.contains('open');
        if (!isOpening) {
            closeSetupPopup();
            return;
        }
        popup.classList.add('open');

        _setupPopupOpener = e ? (e.currentTarget || e.target.closest('button')) : null;
        if (_setupPopupOpener && _setupPopupOpener.setAttribute) {
            _setupPopupOpener.setAttribute('aria-expanded', 'true');
        }
        // On desktop, position popup to the left of the triggering button
        if (window.innerWidth >= 768 && e) {
            const btn = e.currentTarget || e.target.closest('.jump-to-map-btn, .map-control-btn');
            if (btn) {
                const rect = btn.getBoundingClientRect();
                const popupWidth = popup.offsetWidth || 300;
                popup.style.top = rect.top + 'px';
                popup.style.left = (rect.left - popupWidth - 8) + 'px';
                popup.style.right = 'auto';
            }
        }

        // Move focus into the popup once placed (visibility flips to visible immediately on open)
        const firstField = document.getElementById('popupCountSelect');
        if (firstField) firstField.focus({ preventScroll: true });
    }
}

/**
 * Create the setup popup with spot query dropdowns
 */
function createSetupPopup(mapElement) {
    const popup = document.createElement('div');
    popup.className = 'map-control-popup';
    popup.id = 'setupPopup';

    popup.innerHTML = `
        <div class="popup-drag-handle"></div>
        <div class="popup-content setup-popup-content">
            <span>Show</span>
            <div class="popup-field">
                <label class="popup-field-label">Show spots</label>
                <select id="popupCountSelect">
                    <option value="all">All</option>
                    <option value="3">Top 3</option>
                </select>
            </div>
            <span>best fishing spots in</span>
            <div class="popup-field">
                <label class="popup-field-label">Region</label>
                <select id="popupRegionSelect">
                    <option value="all">All Regions</option>
                </select>
            </div>
            <span>,</span>
            <div class="popup-field">
                <label class="popup-field-label">State</label>
                <select id="popupStateSelect">
                    <option value="all">All States</option>
                </select>
            </div>
            <button class="setup-save-btn" onclick="saveSetupAndClose()">Apply Filter</button>
        </div>
    `;

    // Append to body so popup is visible in both map and table views
    document.body.appendChild(popup);
}

/**
 * Save setup settings and close popup
 */
function saveSetupAndClose() {
    const region = document.getElementById('popupRegionSelect')?.value || 'all';
    const state = document.getElementById('popupStateSelect')?.value || 'all';
    const count = document.getElementById('popupCountSelect')?.value || 'all';

    applyMapFilters({ region, state, count });
    closeSetupPopup();
}

// Filter button that last opened the setup popup (for aria-expanded and focus return)
let _setupPopupOpener = null;

/**
 * Close setup popup
 * @param {{returnFocus?: boolean}} [options] - returnFocus: focus the button that opened it
 */
function closeSetupPopup(options) {
    const popup = document.getElementById('setupPopup');
    if (popup) popup.classList.remove('open');
    document.querySelectorAll('#headerFilterBtn, #mySpotsFilterBtn').forEach(function(btn) {
        btn.setAttribute('aria-expanded', 'false');
    });
    if (options && options.returnFocus && _setupPopupOpener && document.contains(_setupPopupOpener)) {
        _setupPopupOpener.focus();
    }
    _setupPopupOpener = null;
}

/**
 * Apply the given filter values to the map, table, and bait shops. Callers (the popup's
 * Apply button, the desktop inline selects, saved-preference prefill) each read their own
 * current values and pass them explicitly — this function never reads a <select> itself,
 * so there's no implicit ordering dependency between "set a select's value" and "apply it".
 */
function applyMapFilters({ region, state, count }) {
    if (!window.allConditionsData) return;
    removeTopSpotCard(); // its "best spot in <area>" no longer matches what the map shows

    showMapWithLocationsByRegion(window.allConditionsData, region, count, state);

    // Update bait shops if they are visible
    if (baitShopsVisible) {
        showBaitShops(region, state);
    }

    // Also update table if the function exists
    if (typeof renderTableByRegion === 'function') {
        renderTableByRegion(window.allConditionsData, region, count, state);
    }

    // Keep the My Spots public-list table in sync if that tab is currently active
    var activeTab = document.querySelector('.tab-content.active');
    if (activeTab && activeTab.id === 'tab-mySpots' && typeof SPOTS !== 'undefined' && typeof SPOTS.renderFilteredTable === 'function') {
        SPOTS.renderFilteredTable(region, state);
    }

    updateHeaderFilterBtn();
    if (typeof SPOTS !== 'undefined' && SPOTS.updateFilterBtn) SPOTS.updateFilterBtn();
}

/**
 * Update the header filter button appearance: full color when a filter is active, gray when default.
 */
function updateHeaderFilterBtn() {
    const btn = document.getElementById('headerFilterBtn');
    if (!btn) return;
    const region = document.getElementById('popupRegionSelect')?.value || 'all';
    const state = document.getElementById('popupStateSelect')?.value || 'all';
    const count = document.getElementById('popupCountSelect')?.value || 'all';
    const isActive = region !== 'all' || state !== 'all' || count !== 'all';
    btn.classList.toggle('filter-active', isActive);
}

/**
 * Populate a <select> with unique regions from allConditions, sorted alphabetically.
 * Shared by the popup, inline (desktop), and settings region selects.
 */
function populateRegionSelect(selectEl, allConditions) {
    if (!selectEl) return;
    const uniqueRegions = [...new Set(allConditions.map(item => item.location.region))].sort();
    let html = '<option value="all">All Regions</option>';
    uniqueRegions.forEach(region => {
        html += `<option value="${escapeHtml(region)}">${escapeHtml(region)}</option>`;
    });
    selectEl.innerHTML = html;
}

/**
 * Populate a <select> with unique states from allConditions, sorted alphabetically.
 * Defaults to Texas selected — matches the app's actual default filter state (see the
 * `defaultState = 'Texas'` initial render calls in main-server.js). Shared by the popup,
 * inline (desktop), and settings state selects.
 */
function populateStateSelect(selectEl, allConditions) {
    if (!selectEl) return;
    const uniqueStates = [...new Set(allConditions.map(item => item.location.state))].sort();
    let html = '<option value="all">All States</option>';
    uniqueStates.forEach(state => {
        const selected = state === 'Texas' ? ' selected' : '';
        html += `<option value="${escapeHtml(state)}"${selected}>${escapeHtml(state)}</option>`;
    });
    selectEl.innerHTML = html;
}

// ==================== Inline Desktop Filter Group ====================
//
// #inlineFilterGroup is a single shared DOM node re-parented between the Angler Map and
// Angler List headers (desktop only — CSS-hidden below 768px in favor of the mobile
// #setupPopup). The popup's selects (#popupRegionSelect/#popupStateSelect/#popupCountSelect)
// remain the single read-source-of-truth for filter state everywhere else in the codebase
// (bait-shop refilter, the mobile-only filter-active button indicator, saved-preference
// prefill) — the inline selects mirror their value into the popup's selects on every change,
// one-way, before applying the filter, so those other call sites keep working unmodified and
// so the popup opens pre-filled if the viewport is resized to mobile mid-session.

const FILTER_GROUP_TARGETS = {
    publicSpots: { containerId: 'mapHeaderControls', anchorId: 'headerLayerBtn' },
    mySpots: { containerId: 'myspotsHeaderControls', anchorId: 'mySpotsAddBtn' }
};

/**
 * Decide which header wrapper #inlineFilterGroup belongs in for a given tab. Pure/data-only
 * (no DOM access) so it's unit-testable without a document.
 */
function getFilterGroupParent(targetTab) {
    return FILTER_GROUP_TARGETS[targetTab] || null;
}

/**
 * Move the shared #inlineFilterGroup node into the header for the given tab. Safe to call
 * repeatedly/idempotently — moving an already-correctly-parented node is a no-op change
 * event-wise since listeners live on the group's children, not the group's parent.
 */
function reparentInlineFilterGroup(targetTab) {
    const target = getFilterGroupParent(targetTab);
    const group = document.getElementById('inlineFilterGroup');
    if (!target || !group) return;

    const container = document.getElementById(target.containerId);
    if (!container) return;

    const anchor = target.anchorId ? document.getElementById(target.anchorId) : null;
    if (anchor) {
        container.insertBefore(group, anchor);
    } else {
        container.appendChild(group);
    }
}

/**
 * Handle a change on any inline Show/Region/State select: mirror the new values into the
 * popup's selects first (see block comment above), then apply immediately (live apply, no
 * explicit "Apply" step on desktop).
 */
function handleInlineFilterChange() {
    const region = document.getElementById('inlineRegionSelect')?.value || 'all';
    const state = document.getElementById('inlineStateSelect')?.value || 'all';
    const count = document.getElementById('inlineCountSelect')?.value || 'all';

    const popupRegion = document.getElementById('popupRegionSelect');
    const popupState = document.getElementById('popupStateSelect');
    const popupCount = document.getElementById('popupCountSelect');
    if (popupRegion) popupRegion.value = region;
    if (popupState) popupState.value = state;
    if (popupCount) popupCount.value = count;

    applyMapFilters({ region, state, count });
}

/**
 * One-time wiring for the desktop-only inline filter group and layer select. Listener
 * attachment must happen exactly once, here — never inside switchTab()/reparentInlineFilterGroup(),
 * which only ever moves the already-wired node between headers. Re-attaching on every tab
 * switch would stack listeners (a normal map ↔ list ↔ map session would fire the update
 * cascade multiple times per change).
 */
function initInlineControls() {
    ['inlineRegionSelect', 'inlineStateSelect', 'inlineCountSelect'].forEach(function(id) {
        const select = document.getElementById(id);
        if (select) select.addEventListener('change', handleInlineFilterChange);
    });

    populateMapLayerSelect();
    const layerSelect = document.getElementById('mapLayerSelect');
    if (layerSelect) layerSelect.addEventListener('change', handleMapLayerSelectChange);

    // Exercise the same placement logic switchTab() will use later, for whichever tab is
    // active at load time (the group is already statically rendered there via app.ejs, so
    // this is a harmless no-op on the common path).
    const activeTab = document.querySelector('.tab-content.active');
    if (activeTab) reparentInlineFilterGroup(activeTab.id.replace(/^tab-/, ''));
}

/**
 * Populate the desktop layer <select> with only the layers actually available right now —
 * mirrors the hasMaptilerKey() gating getLayerCycle() already applies to the mobile cycle
 * button. CONFIG.MAP_LAYERS always lists both layers regardless of runtime key availability,
 * so populating from it directly would offer a "Satellite" option that silently does nothing
 * for users without an available key.
 */
function populateMapLayerSelect() {
    const select = document.getElementById('mapLayerSelect');
    if (!select) return;
    select.innerHTML = getLayerCycle().map(function(id) {
        return `<option value="${id}">${CONFIG.MAP_LAYERS[id].name}</option>`;
    }).join('');
    select.value = currentLayer;
}

/**
 * Handle a change on the desktop layer <select> — bypasses the cycle/toggle logic used by
 * #headerLayerBtn (there's nothing to "cycle", the user picked a specific layer), but still
 * keeps #headerLayerBtn's icon in sync via the shared updateLayerButtonIcon() helper so it
 * doesn't go stale if the viewport is resized to mobile mid-session.
 */
function handleMapLayerSelectChange(e) {
    const value = e.target.value;
    if (!fishingMap || value === currentLayer) return;
    switchBaseLayer(value);
    updateLayerButtonIcon(document.getElementById('headerLayerBtn'));
    saveMapSettings();
}

/**
 * Fetch and show bait shop markers filtered by region and state
 */
async function showBaitShops(selectedRegion = 'all', selectedState = 'all') {
    if (!fishingMap) return;

    // Store current filter values
    currentBaitShopRegion = selectedRegion;
    currentBaitShopState = selectedState;

    // Clear existing bait shop markers first
    hideBaitShops(false); // Pass false to not reset baitShopsVisible

    // Fetch data if not cached
    if (!baitShopsData) {
        try {
            const response = await fetch(`${API_CLIENT.baseURL}/bait-shops`);
            const result = await response.json();
            if (result.success) {
                baitShopsData = result.data;
            } else {
                console.error('Failed to fetch bait shops:', result.error);
                return;
            }
        } catch (error) {
            console.error('Error fetching bait shops:', error);
            return;
        }
    }

    // Filter bait shops by state first, then by region
    let filteredShops = baitShopsData;
    if (selectedState !== 'all') {
        filteredShops = filteredShops.filter(shop => shop.state === selectedState);
    }
    if (selectedRegion !== 'all') {
        filteredShops = filteredShops.filter(shop => shop.region === selectedRegion);
    }
    // Drop entries with missing/invalid coordinates
    filteredShops = filteredShops.filter(shop => isFinite(shop.lat) && isFinite(shop.lon));

    // Get initial size based on current zoom
    const initialSize = getBaitShopSizeForZoom(fishingMap.getZoom());
    const baitShopIcon = getCachedIcon('baitshop', initialSize);

    // Create feature group OFF-map, add all markers, then attach once (single render)
    baitShopGroup = L.featureGroup();

    filteredShops.forEach(shop => {
        const marker = L.marker([shop.lat, shop.lon], { icon: baitShopIcon })
            .addTo(baitShopGroup); // Adds to group (not yet on map)

        marker.on('click', function(e) {
            L.DomEvent.stopPropagation(e);

            const isDesktop = window.innerWidth >= 768;
            const mapWidth = fishingMap.getSize().x;
            const offsetLng = isDesktop ? (mapWidth * 0.22) * (360 / Math.pow(2, 17 + 8)) : 0;

            fishingMap.flyTo([shop.lat, shop.lon - offsetLng], 17, {
                duration: 0.5
            });

            const { sidebar, sidebarContent } = getSidebarElements();

            const sidebarHTML = buildBaitShopSidebarHTML(shop);
            sidebarContent.innerHTML = sidebarHTML;

            sidebar.scrollTop = 0;
            sidebarContent.scrollTop = 0;
            sidebar.classList.add('open');
            closeLayersPanel();
        });

        baitShopMarkers.push(marker);
    });

    // Single DOM operation: all bait shop markers appear at once
    baitShopGroup.addTo(fishingMap);

    baitShopsVisible = true;

    // Sync Layers panel checkbox
    const baitShopCb = document.getElementById('mapLayersBaitShopsCheckbox');
    if (baitShopCb) {
        baitShopCb.checked = true;
    }
}

/**
 * Hide bait shop markers
 * @param {boolean} resetVisible - Whether to reset baitShopsVisible flag (default: true)
 */
function hideBaitShops(resetVisible = true) {
    if (fishingMap && baitShopGroup) {
        fishingMap.removeLayer(baitShopGroup);
        baitShopGroup = null;
    }
    baitShopMarkers = [];
    if (resetVisible) {
        baitShopsVisible = false;

        // Sync Layers panel checkbox
        const baitShopCb = document.getElementById('mapLayersBaitShopsCheckbox');
        if (baitShopCb) {
            baitShopCb.checked = false;
        }
    }
}

/**
 * Format phone number as (xxx) xxx-xxxx
 */
function formatPhoneNumber(phone) {
    if (!phone) return '';
    // Remove all non-digit characters
    const digits = phone.replace(/\D/g, '');
    // Format as (xxx) xxx-xxxx
    if (digits.length === 10) {
        return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
    }
    // If 11 digits starting with 1, skip the first digit
    if (digits.length === 11 && digits[0] === '1') {
        return `(${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
    }
    // Return original if format doesn't match
    return phone;
}

/**
 * Build sidebar HTML for bait shop
 */
function buildBaitShopSidebarHTML(shop) {
    const days = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    const todayIndex = new Date().getDay();

    // Build hours list
    let hoursHTML = '';
    if (shop.hours) {
        hoursHTML = days.map((day, index) => {
            const isToday = index === todayIndex;
            const hours = shop.hours[day] || 'Closed';
            return `<div class="bait-shop-hours-row ${isToday ? 'today' : ''}">
                <span class="day-name">${dayNames[index]}</span>
                <span class="day-hours">${hours}</span>
            </div>`;
        }).join('');
    }

    // Create Google Maps directions URL
    const mapsUrl = `https://www.google.com/maps/dir/?api=1&destination=${shop.lat},${shop.lon}`;

    return `
        <div class="bait-shop-sidebar">
            <div class="sidebar-popup-title">
                <h3>${escapeHtml(shop.name)}</h3>
                <div class="sidebar-popup-title__actions">
                    <a href="${mapsUrl}" target="_blank" rel="noopener noreferrer" class="navigate-btn-inline">
                        <img src="./img/google-navigate.png" alt="Navigate" />
                    </a>
                    <a role="button" class="sidebar-title-close-btn navigate-btn-inline" title="Close">
                        <img src="./img/close-button.png" alt="Close" />
                    </a>
                </div>
            </div>

            <div class="bait-shop-section">
                <h4>Hours</h4>
                <div class="bait-shop-hours-list">
                    ${hoursHTML}
                </div>
            </div>
        </div>
    `;
}

;
/* === js/map-overlays.js === */
/**
 * Map Overlays Module
 * Config-driven overlay system for bathymetry and nautical chart layers
 *
 * LOADED BY: app.html / app.ejs via <script src="/js/map-overlays.js">
 * USED BY: map.js → initMapOverlays() called from createLayersControl()
 * DEPENDS ON: Leaflet.js (L global), config-frontend.js (CONFIG.BED_TYPE_CLASSES)
 * EXPORTS: initMapOverlays(), toggleOverlay(), getOverlayState(), updateOverlayBlendMode(),
 *          canUseOverlay(), enforceOverlayTier(), OVERLAY_CONFIG as globals
 */

// ── Overlay Configuration ────────────────────────────────────────────────────
// Each entry defines a toggleable map layer. Add new layers here.

const OVERLAY_CONFIG = [
    {
        id: 'nauticalCharts',
        name: 'Contour',
        type: 'wms',
        blendMode: 'multiply',
        url: 'https://gis.charttools.noaa.gov/arcgis/rest/services/MCS/NOAAChartDisplay/MapServer/exts/MaritimeChartService/WMSServer',
        options: {
            // 2=depths/contours, 3=seabed/wrecks
            // Excluded: 0=labels, 1=land, 4=shipping, 5=regulatory, 6=buoys, 7+=unused
            layers: '2,3',
            format: 'image/png',
            transparent: true,
            opacity: 0.85,
            maxZoom: 18,
            maxNativeZoom: 17,
            tileSize: 512,
            zoomOffset: -1,
            updateWhenZooming: false,
            updateWhenIdle: true,
            keepBuffer: 4,
            attribution: 'NOAA Office of Coast Survey'
        }
    },
    // Rain Radar removed — commented out for future reference
    // {
    //     id: 'weatherRadar',
    //     name: 'Rain Radar',
    //     icon: './img/rain-filter.png',
    //     type: 'rainviewer',
    //     options: { opacity: 0.6, maxZoom: 18, maxNativeZoom: 12,
    //         updateWhenZooming: false, updateWhenIdle: true, keepBuffer: 2 }
    // },
    {
        id: 'hiresBathymetry',
        name: '1ft Contour',
        proOnly: true,
        type: 'pmtiles',
        render: 'contours',
        blendMode: 'multiply',
        url: 'https://tiles.reeltalkfishing.com/contours_gulf.pmtiles',
        style: {
            color: '#1a6ea8',
            major10Weight: 2.5,
            major5Weight: 1.8,
            major3Weight: 1.3,
            minorWeight: 0.7,
            major10Opacity: 1.0,
            major5Opacity: 0.85,
            major3Opacity: 0.7,
            minorOpacity: 0.45,
            label5MinZoom: 12,  // 5ft labels show zoom 12–13
            label3MinZoom: 14,  // 3ft labels show zoom 14–15
            label1MinZoom: 16,  // 1ft labels show zoom 16+
            labelFontSize: 11,
            label3FontSize: 10,
            label1FontSize: 9,
            labelFontWeight: '600',
            labelStroke: '#ffffff',
            labelStrokeWidth: 2
        },
        options: {
            minZoom: 9,
            maxZoom: 22,
            maxDataZoom: 16,
            attribution: 'NOAA CUDEM'
        }
    },
    {
        id: 'bedType',
        name: 'Bed Type',
        proOnly: true,
        type: 'pmtiles',
        render: 'fill',
        pane: 'fill',
        url: 'https://tiles.reeltalkfishing.com/bedtype_us_v3.pmtiles',
        dataLayer: 'bed_type',
        classes: CONFIG.BED_TYPE_CLASSES,
        legendNote: 'General bottom type from survey samples. Not for navigation.',
        opacity: 0.7,
        options: { minZoom: 6, maxZoom: 22, maxDataZoom: 14, attribution: 'USGS usSEABED, NOAA ENC' }
    }
];

// ── State ────────────────────────────────────────────────────────────────────

let _overlayMap = null;              // Leaflet map reference
const _layerInstances = {};          // id → L.layer instance (created lazily)
const _activeOverlays = new Set();   // ids of currently visible overlays
const _loadingOverlays = new Set();  // ids of overlays currently loading tiles
const _hiddenBounds = {};            // id → bounds string when overlay was hidden
// let _radarLegend = null;          // Rain Radar removed
let _legendContainer = null;         // Shared container for overlay legends

// ── Public API ───────────────────────────────────────────────────────────────

/**
 * Initialize the overlay system. Creates a custom pane below markers.
 * @param {L.Map} map - Leaflet map instance
 */
function initMapOverlays(map) {
    if (!map) {
        console.error('[Overlays] No map instance provided');
        return;
    }
    _overlayMap = map;

    // Create panes per blend mode (below markers, z-index 600)
    // 'mapOverlayPane' = normal blend, 'mapOverlayPane-multiply' = multiply blend,
    // 'mapOverlayPane-fill' = area fills under the contour lines, never re-blended on satellite
    if (!map.getPane('mapOverlayPane')) {
        map.createPane('mapOverlayPane');
        map.getPane('mapOverlayPane').style.zIndex = 250;
    }
    if (!map.getPane('mapOverlayPane-multiply')) {
        map.createPane('mapOverlayPane-multiply');
        map.getPane('mapOverlayPane-multiply').style.zIndex = 250;
        map.getPane('mapOverlayPane-multiply').style.mixBlendMode = 'multiply';
    }
    if (!map.getPane('mapOverlayPane-fill')) {
        map.createPane('mapOverlayPane-fill');
        map.getPane('mapOverlayPane-fill').style.zIndex = 240;
    }

    console.log('[Overlays] Initialized with', OVERLAY_CONFIG.length, 'configured layers');
}

/**
 * Update overlay blend mode based on the active base layer.
 * Satellite (dark) → screen (contour lines glow on dark imagery)
 * Voyager (light) → multiply (contour lines darken into light map)
 * @param {string} baseLayerName - 'voyager' or 'satellite'
 */
function updateOverlayBlendMode(baseLayerName) {
    if (!_overlayMap) return;
    const pane = _overlayMap.getPane('mapOverlayPane-multiply');
    if (!pane) return;
    pane.style.mixBlendMode = baseLayerName === 'satellite' ? 'screen' : 'multiply';
}

/**
 * Whether the current user may turn this overlay on.
 * Pro layers need a pro/elite tier or the admin role (never `tier !== 'free'`).
 * @param {Object} config - Entry from OVERLAY_CONFIG
 * @returns {boolean}
 */
function canUseOverlay(config) {
    if (!config.proOnly) return true;
    if (typeof AUTH_CLIENT === 'undefined') return false;
    return AUTH_CLIENT.membershipTier === 'pro' || AUTH_CLIENT.membershipTier === 'elite' || AUTH_CLIENT.isAdmin();
}

/**
 * Hide every active overlay the current tier no longer allows (called on tier change).
 * Does not touch saved settings, so the layer comes back if the user re-subscribes.
 */
function enforceOverlayTier() {
    OVERLAY_CONFIG.forEach(config => {
        if (_activeOverlays.has(config.id) && !canUseOverlay(config)) {
            toggleOverlay(config.id);
        }
    });
}

/**
 * Toggle an overlay on or off
 * @param {string} id - Overlay id from OVERLAY_CONFIG
 * @returns {boolean} New visibility state
 */
function toggleOverlay(id) {
    if (!_overlayMap) {
        console.error('[Overlays] Map not initialized — call initMapOverlays() first');
        return false;
    }

    const config = OVERLAY_CONFIG.find(c => c.id === id);
    if (!config) {
        console.error('[Overlays] Unknown overlay id:', id);
        return false;
    }

    if (_activeOverlays.has(id)) {
        // Hide overlay — use container hiding to preserve tile cache for fast re-show
        const layer = _layerInstances[id];
        if (layer) {
            // Store viewport bounds so we can skip redraw if unchanged on re-show
            _hiddenBounds[id] = _overlayMap.getBounds().toBBoxString();
            const container = layer.getContainer ? layer.getContainer() : null;
            if (container) {
                container.style.display = 'none';
            } else if (_overlayMap.hasLayer(layer)) {
                _overlayMap.removeLayer(layer);
            }
        }
        // Clear polling interval to prevent memory leak
        if (layer && layer._refreshInterval) {
            clearInterval(layer._refreshInterval);
            layer._refreshInterval = null;
        }
        _activeOverlays.delete(id);
        _loadingOverlays.delete(id);
        _updateLoadingUI(id, false);
        _showClassLegend(id, false);
        // if (id === 'weatherRadar') _toggleRadarLegend(false); // Rain Radar removed
        console.log('[Overlays] Disabled:', config.name);
        return false;
    } else {
        // Every enable path (checkbox, saved-state restore, Settings sync) goes through here
        if (!canUseOverlay(config)) return false;
        // Create layer instance if needed (lazy)
        if (!_layerInstances[id]) {
            const layer = _createLayer(config);
            if (!layer) return false;
            _layerInstances[id] = layer;
        }
        const layer = _layerInstances[id];
        // rainviewer restart removed — Rain Radar feature removed
        // Show overlay — re-show hidden container (instant) or add to map (first time)
        const container = layer.getContainer ? layer.getContainer() : null;
        if (container && _overlayMap.hasLayer(layer)) {
            container.style.display = '';
            // Only redraw if viewport changed while hidden (avoids unnecessary tile refetch)
            const prevBounds = _hiddenBounds[id];
            if (!prevBounds || prevBounds !== _overlayMap.getBounds().toBBoxString()) {
                layer.redraw();
            }
            delete _hiddenBounds[id];
        } else {
            layer.addTo(_overlayMap);
        }
        _activeOverlays.add(id);
        _showClassLegend(id, true);
        // if (id === 'weatherRadar') _toggleRadarLegend(true); // Rain Radar removed
        console.log('[Overlays] Enabled:', config.name);
        return true;
    }
}

/**
 * Set overlay visibility to a specific state (used for restoring from localStorage)
 * @param {string} id - Overlay id
 * @param {boolean} visible - Desired visibility
 */
function setOverlayVisible(id, visible) {
    const isCurrentlyVisible = _activeOverlays.has(id);
    if (visible && !isCurrentlyVisible) {
        toggleOverlay(id);
    } else if (!visible && isCurrentlyVisible) {
        toggleOverlay(id);
    }
}

/**
 * Get current overlay visibility state for persistence
 * @returns {Object} Map of id → boolean
 */
function getOverlayState() {
    const state = {};
    OVERLAY_CONFIG.forEach(config => {
        state[config.id] = _activeOverlays.has(config.id);
    });
    return state;
}

// ── Layer Factory ────────────────────────────────────────────────────────────

// Contour style keys validatePmtilesConfig() requires for render: 'contours'
const CONTOUR_STYLE_KEYS = ['color',
    'major10Weight', 'major5Weight', 'major3Weight', 'minorWeight',
    'major10Opacity', 'major5Opacity', 'major3Opacity', 'minorOpacity',
    'label5MinZoom', 'label3MinZoom', 'label1MinZoom',
    'labelFontSize', 'label3FontSize', 'label1FontSize',
    'labelFontWeight', 'labelStroke', 'labelStrokeWidth'];

/**
 * Validate a pmtiles overlay config entry.
 * render 'contours' needs the contour style block; render 'fill' needs dataLayer,
 * classes (key → { name, color }) and a numeric opacity.
 * @param {Object} config - Entry from OVERLAY_CONFIG with type 'pmtiles'
 * @returns {string|null} Error message, or null if valid
 */
function validatePmtilesConfig(config) {
    if (!config.url) return 'missing url';
    if (config.render === 'contours') {
        if (!config.style) return 'missing style block';
        for (const key of CONTOUR_STYLE_KEYS) {
            if (config.style[key] === undefined) return `style missing: ${key}`;
        }
    } else if (config.render === 'fill') {
        if (!config.dataLayer) return 'missing dataLayer';
        if (!config.classes || !Object.keys(config.classes).length) return 'missing classes';
        for (const [key, cls] of Object.entries(config.classes)) {
            if (!cls || !cls.name || !cls.color) return `class ${key} needs name and color`;
        }
        if (typeof config.opacity !== 'number') return 'missing opacity';
    } else {
        return `unknown render: ${config.render}`;
    }
    if (!config.options) return 'missing options block';
    if (config.options.minZoom === undefined) return 'options missing: minZoom';
    if (config.options.maxZoom === undefined) return 'options missing: maxZoom';
    // maxDataZoom is optional — defaults to maxZoom if omitted
    return null;
}

/**
 * Paint and label rules for depth contours (render: 'contours').
 * @param {Object} config - pmtiles entry from OVERLAY_CONFIG
 * @returns {{ paintRules: Array, labelRules: Array }}
 */
function _contourRules(config) {
    const s = config.style;
    return {
        paintRules: [{
            dataLayer: 'depth_contours',
            symbolizer: new protomapsL.LineSymbolizer({
                color: s.color,
                perFeature: true,
                width: (_zoom, feature) => {
                    if (feature.props.major10) return s.major10Weight;
                    if (feature.props.major5)  return s.major5Weight;
                    if (feature.props.major3)  return s.major3Weight;
                    return s.minorWeight;
                },
                opacity: (_zoom, feature) => {
                    if (feature.props.major10) return s.major10Opacity;
                    if (feature.props.major5)  return s.major5Opacity;
                    if (feature.props.major3)  return s.major3Opacity;
                    return s.minorOpacity;
                }
            })
        }],
        labelRules: [
            {
                // 5ft labels: zoom 12+, persist at all higher zooms
                dataLayer: 'depth_labels_5ft',
                minzoom: s.label5MinZoom,
                symbolizer: new protomapsL.CenteredTextSymbolizer({
                    labelProps: ['label'],
                    fill: s.color,
                    stroke: s.labelStroke,
                    width: s.labelStrokeWidth,
                    fontSize: s.labelFontSize,
                    fontWeight: s.labelFontWeight,
                    fontFamily: 'sans-serif'
                })
            },
            {
                // 3ft labels: zoom 14+, persist at all higher zooms
                dataLayer: 'depth_labels_3ft',
                minzoom: s.label3MinZoom,
                symbolizer: new protomapsL.CenteredTextSymbolizer({
                    labelProps: ['label'],
                    fill: s.color,
                    stroke: s.labelStroke,
                    width: s.labelStrokeWidth,
                    fontSize: s.label3FontSize,
                    fontWeight: s.labelFontWeight,
                    fontFamily: 'sans-serif'
                })
            },
            {
                // 1ft labels: zoom 16+ (highest detail, sight-fishing range)
                dataLayer: 'depth_labels_1ft',
                minzoom: s.label1MinZoom,
                symbolizer: new protomapsL.CenteredTextSymbolizer({
                    labelProps: ['label'],
                    fill: s.color,
                    stroke: s.labelStroke,
                    width: s.labelStrokeWidth,
                    fontSize: s.label1FontSize,
                    fontWeight: s.labelFontWeight,
                    fontFamily: 'sans-serif'
                })
            }
        ]
    };
}

/**
 * Paint rules for class fills (render: 'fill'). Each feature carries its class key in
 * `c`. A feature with an unknown class is filtered out, not drawn in a default colour.
 * @param {Object} config - pmtiles entry from OVERLAY_CONFIG
 * @returns {{ paintRules: Array, labelRules: Array }}
 */
function _fillRules(config) {
    const { classes } = config;
    return {
        paintRules: [{
            dataLayer: config.dataLayer,
            filter: (_zoom, feature) => Object.prototype.hasOwnProperty.call(classes, feature.props.c),
            symbolizer: new protomapsL.PolygonSymbolizer({
                perFeature: true,
                fill: (_zoom, feature) => classes[feature.props.c].color,
                opacity: config.opacity
            })
        }],
        labelRules: []
    };
}

/**
 * Create a Leaflet layer instance from config, with loading event hooks
 * @param {Object} config - Entry from OVERLAY_CONFIG
 * @returns {L.Layer|null}
 */
function _createLayer(config) {
    const pane = config.pane ? `mapOverlayPane-${config.pane}`
        : config.blendMode ? `mapOverlayPane-${config.blendMode}`
        : 'mapOverlayPane';
    const opts = { ...config.options, pane };
    let layer;

    switch (config.type) {
        case 'tileLayer':
            layer = L.tileLayer(config.url, opts);
            break;

        case 'wms':
            layer = L.tileLayer.wms(config.url, opts);
            break;

        // 'rainviewer' case removed — Rain Radar feature removed
        // case 'rainviewer': ...


        case 'pmtiles': {
            const configError = validatePmtilesConfig(config);
            if (configError) {
                console.error('[Overlays] Invalid pmtiles config:', configError);
                return null;
            }
            if (typeof protomapsL === 'undefined') {
                console.error('[Overlays] protomaps-leaflet not loaded — check script tag in app.ejs');
                return null;
            }
            const { paintRules, labelRules } = config.render === 'fill' ? _fillRules(config) : _contourRules(config);
            layer = protomapsL.leafletLayer({
                url: config.url,
                pane,
                minZoom: config.options.minZoom,
                maxZoom: config.options.maxZoom,
                maxDataZoom: config.options.maxDataZoom || config.options.maxZoom,
                attribution: config.options.attribution,
                paintRules,
                labelRules
            });
            break;
        }

        default:
            console.error('[Overlays] Unknown layer type:', config.type);
            return null;
    }

    // Attach tile loading/loaded events for loading indicator + timeout
    let _loadTimeout = null;
    layer.on('loading', () => {
        _loadingOverlays.add(config.id);
        _updateLoadingUI(config.id, true);
        // Start a 15s timeout — if tiles haven't loaded by then, the service is likely down
        clearTimeout(_loadTimeout);
        _loadTimeout = setTimeout(() => {
            if (_loadingOverlays.has(config.id)) {
                console.warn('[Overlays]', config.name, 'timed out after 15s — still loading');
                // Don't disable — slow WMS (e.g. NOAA) may still deliver tiles
            }
        }, 15000);
    });
    layer.on('load', () => {
        clearTimeout(_loadTimeout);
        _loadingOverlays.delete(config.id);
        _updateLoadingUI(config.id, false);
    });
    layer.on('tileerror', () => {
        clearTimeout(_loadTimeout);
        if (_activeOverlays.has(config.id)) {
            console.warn('[Overlays]', config.name, 'tile load error');
            _handleOverlayError(config.id, config.name);
        }
    });

    return layer;
}

/**
 * Fetch latest radar timestamp from RainViewer API and update tile URL
 * @param {L.TileLayer} layer - Leaflet tile layer to update
 */
function _fetchRainViewerTimestamp(layer) {
    fetch('https://api.rainviewer.com/public/weather-maps.json')
        .then(r => r.json())
        .then(data => {
            const frames = data.radar && data.radar.past;
            if (!frames || !frames.length) {
                console.error('[Overlays] No RainViewer radar frames available');
                return;
            }
            // Use the most recent frame
            const latest = frames[frames.length - 1];
            // RainViewer tile URL: /v2/radar/{ts}/{size}/{z}/{x}/{y}/{color}/{smooth}.png
            // color 6 = universal blue, smooth 1 = smoothed edges
            const url = `https://tilecache.rainviewer.com${latest.path}/256/{z}/{x}/{y}/6/1_1.png`;
            layer.setUrl(url);
            layer._rainviewerReady = true;
            console.log('[Overlays] RainViewer radar loaded, frame:', new Date(latest.time * 1000).toLocaleTimeString());
        })
        .catch(err => console.error('[Overlays] RainViewer fetch failed:', err));
}

// ── Overlay Legends ──────────────────────────────────────────────────────────

/**
 * Get or create the shared container for overlay color-scale legends.
 * Both radar and SST legends live inside this flex-column container so they stack.
 */
function _getLegendContainer() {
    if (!_legendContainer) {
        _legendContainer = document.createElement('div');
        _legendContainer.className = 'overlay-legends';
        _overlayMap.getContainer().appendChild(_legendContainer);
    }
    return _legendContainer;
}

/**
 * Show or hide the radar intensity scale on the map
 * @param {boolean} show - Whether to show the legend
 */
function _toggleRadarLegend(show) {
    if (show && !_radarLegend) {
        _radarLegend = document.createElement('div');
        _radarLegend.className = 'radar-legend';
        _radarLegend.innerHTML = `
            <div class="radar-legend-row">
                <span class="radar-legend-label">Light</span>
                <div class="radar-legend-bar"></div>
                <span class="radar-legend-label">Heavy</span>
            </div>
        `;
        _getLegendContainer().appendChild(_radarLegend);
    } else if (show && _radarLegend) {
        _radarLegend.style.display = '';
    } else if (!show && _radarLegend) {
        _radarLegend.style.display = 'none';
    }
}

/**
 * Show or hide an overlay's colour key in the strip under the map (built by map.js
 * createMapLegend()). Overlays without `classes` have no key, so this does nothing.
 * @param {string} id - Overlay id
 * @param {boolean} show - Whether the overlay is now on
 */
function _showClassLegend(id, show) {
    const legend = document.querySelector(`.legend-classes[data-legend-for="${id}"]`);
    if (legend) legend.hidden = !show;
}

// ── Error Handling ───────────────────────────────────────────────────────────

/**
 * Handle overlay load failure: auto-disable the overlay, uncheck its checkbox, show toast
 * @param {string} id - Overlay id
 * @param {string} name - Display name for the toast message
 */
function _handleOverlayError(id, name) {
    // Auto-disable the broken overlay
    if (_activeOverlays.has(id)) {
        toggleOverlay(id);
    }
    // Uncheck the checkbox in the Layers panel
    const cb = document.querySelector(`input[data-overlay-id="${id}"]`);
    if (cb) cb.checked = false;
    // Show a brief toast to the user
    _showOverlayToast(`${name} layer unavailable — try again later`);
}

/**
 * Show a brief toast notification near the map legend area
 * @param {string} message - Message to display
 * @param {number} duration - Duration in ms (default 4000)
 */
function _showOverlayToast(message, duration = 4000) {
    // Reuse or create toast element
    let toast = document.getElementById('overlay-toast');
    if (!toast) {
        toast = document.createElement('div');
        toast.id = 'overlay-toast';
        toast.className = 'overlay-toast';
        document.body.appendChild(toast);
    }
    toast.textContent = message;
    toast.classList.add('show');
    clearTimeout(toast._hideTimer);
    toast._hideTimer = setTimeout(() => toast.classList.remove('show'), duration);
}

// ── Cleanup ──────────────────────────────────────────────────────────────────

/**
 * Clean up all overlay resources (intervals, layers, DOM elements).
 * Called on page unload to prevent memory leaks.
 */
function cleanupOverlays() {
    // Clear all polling intervals
    Object.values(_layerInstances).forEach(layer => {
        if (layer && layer._refreshInterval) {
            clearInterval(layer._refreshInterval);
            layer._refreshInterval = null;
        }
    });
    // Remove all layers from map (both active and hidden)
    Object.entries(_layerInstances).forEach(([id, layer]) => {
        if (layer && _overlayMap && _overlayMap.hasLayer(layer)) {
            _overlayMap.removeLayer(layer);
        }
    });
    _activeOverlays.clear();
    _loadingOverlays.clear();
}

// Prevent interval leaks on page unload/refresh
window.addEventListener('pagehide', cleanupOverlays);

// ── Loading UI ───────────────────────────────────────────────────────────────

/**
 * Update the loading spinner on the overlay's checkbox label
 * @param {string} id - Overlay id
 * @param {boolean} loading - Whether tiles are loading
 */
function _updateLoadingUI(id, loading) {
    const cb = document.querySelector(`input[data-overlay-id="${id}"]`);
    if (!cb) return;
    const label = cb.closest('label');
    if (!label) return;

    if (loading) {
        label.classList.add('overlay-loading');
    } else {
        label.classList.remove('overlay-loading');
    }
}

// Export for Node.js (testing)
if (typeof module !== 'undefined' && module.exports) {
    module.exports = {
        OVERLAY_CONFIG,
        canUseOverlay,
        toggleOverlay,
        setOverlayVisible,
        getOverlayState,
        initMapOverlays,
        enforceOverlayTier,
        validatePmtilesConfig,
        updateOverlayBlendMode,
        _createLayer,
        _fillRules
    };
}
